<template>
    <LeftNavShell
        aria-label="Sidebar"
        :background-class="currentUser.isImpersonating ? 'bg-pink-50' : 'md:bg-white'"
        :collapsed-class="isSignalRoute ? 'w-0 overflow-hidden' : 'w-24 border-r'"
        :class="{
            'ring-4 ring-pink-200': currentUser.isImpersonating,
        }"
    >
        <template #default="{ isExpanded }">
            <LeftNavImpersonationIndicator
                v-if="currentUser.isImpersonating"
                :account-name="currentUser.account_name"
                :is-expanded="isExpanded"
                @return="currentUser.returnToOriginalAccount()"
            />
            <div class="relative min-h-0 flex-1">
                <div
                    v-show="!isScrolledToBottom"
                    class="pointer-events-none absolute right-3 bottom-0 left-0 z-10 h-8"
                    :class="
                        currentUser.isImpersonating
                            ? 'bg-gradient-to-t from-pink-50 to-transparent'
                            : 'bg-gradient-to-t from-white to-transparent'
                    "
                />
                <div
                    v-show="!isScrolledToTop"
                    class="pointer-events-none absolute top-0 right-3 left-0 z-10 h-8"
                    :class="
                        currentUser.isImpersonating
                            ? 'bg-gradient-to-b from-pink-50 to-transparent'
                            : 'bg-gradient-to-b from-white to-transparent'
                    "
                />
                <div
                    ref="scrollEl"
                    class="no-scrollbar flex h-full flex-col gap-3 overflow-x-clip overflow-y-auto"
                    @scroll="onNavScroll"
                >
                    <LeftNavItem
                        :item="{
                            title: 'Home',
                            to: '/home',
                        }"
                        :is-expanded="isExpanded"
                    >
                        <HouseIcon />
                    </LeftNavItem>

                    <NavExpander v-if="currentUser.has.customReports" :is-expanded="isExpanded">
                        <template #default="{ toggleChildren, isActive, setActive }">
                            <LeftNavItem
                                :item="{
                                    title: 'Custom Reports',
                                    to: '/custom-reports',
                                }"
                                :is-expanded="isExpanded"
                                @active-route-change="setActive($event)"
                            >
                                <PresentationIcon />
                                <template #expander>
                                    <ButtonTriangle class="ml-2" :is-active="isActive" @click="toggleChildren" />
                                </template>
                            </LeftNavItem>
                        </template>
                        <template #children>
                            <ul>
                                <LeftNavItemChild
                                    v-for="c in currentUser.customReports"
                                    :key="c.param"
                                    :item="{
                                        title: c.key,
                                        to: {
                                            name: 'custom-reports-embed',
                                            params: {
                                                embed: c.param,
                                            },
                                        },
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>
                        </template>
                    </NavExpander>

                    <NavExpander v-if="currentUser.has.signal" :is-expanded="isExpanded">
                        <template #default="{ setActive }">
                            <LeftNavItem
                                :item="{
                                    title: 'Signal',
                                    to: { name: 'signal-index' },
                                }"
                                :is-expanded="isExpanded"
                                @active-route-change="setActive($event)"
                            >
                                <SignalLogoIcon stroke-width="1.5" class="h-full w-full [&_path]:opacity-100" />
                            </LeftNavItem>
                        </template>
                    </NavExpander>

                    <LeftNavItem
                        :item="{
                            title: 'API Documentation',
                            to: '/api-docs',
                        }"
                        :is-expanded="isExpanded"
                    >
                        <Code />
                    </LeftNavItem>

                    <LeftNavItem
                        v-if="currentUser.has.amplitude"
                        :item="{
                            title: 'Amplitude',
                            to: '/amplitude',
                        }"
                        :is-expanded="isExpanded"
                    >
                        <AmplitudeLogoIcon />
                    </LeftNavItem>

                    <LeftNavItem
                        v-if="currentUser.has.marketShare || currentUser.has.signal"
                        :item="{
                            title: 'Market Share',
                            to: '/market-share',
                        }"
                        :is-expanded="isExpanded"
                    >
                        <ChartPieIcon />
                    </LeftNavItem>

                    <NavExpander v-if="adminUser || currentUser.isDasboardsMenuConfigured" :is-expanded="isExpanded">
                        <template #default="{ toggleChildren, isActive, setActive }">
                            <LeftNavItem
                                :item="{
                                    title: 'Behavioral Health Analytics',
                                    to: `/dashboards`,
                                }"
                                :is-expanded="isExpanded"
                                @active-route-change="setActive($event)"
                            >
                                <ChartColumnBigIcon />
                                <template #expander>
                                    <ButtonTriangle class="ml-2" :is-active="isActive" @click="toggleChildren" />
                                </template>
                            </LeftNavItem>
                        </template>
                        <template #children>
                            <ul v-if="adminUser || currentUser.dashboard_allowed === 'radiology'">
                                <LeftNavItemChild
                                    :item="{
                                        title: 'Radiology',
                                        to: '/dashboards/radiology',
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>

                            <ul v-if="adminUser || currentUser.isDashboardConfigured('behavioral-next')">
                                <LeftNavItemChild
                                    :item="{
                                        title: 'Behavioral Health Next',
                                        to: '/dashboards/behavioral-next',
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>

                            <ul v-if="adminUser || currentUser.isDashboardConfigured('behavioral-compare')">
                                <LeftNavItemChild
                                    :item="{
                                        title: 'Behavioral Health Compare',
                                        to: '/dashboards/behavioral-compare',
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>

                            <ul v-if="adminUser || currentUser.isDashboardConfigured('referral-analytics')">
                                <LeftNavItemChild
                                    :item="{
                                        title: 'Referral Analytics',
                                        to: '/dashboards/referral-analytics',
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>
                        </template>
                    </NavExpander>

                    <NavExpander v-if="adminUser || featureFlags.employerReports" :is-expanded="isExpanded">
                        <template #default="{ toggleChildren, isActive, setActive }">
                            <LeftNavItem
                                :item="{
                                    title: 'Employer Analytics',
                                    to: '/employer/employer-network-report',
                                }"
                                :is-expanded="isExpanded"
                                @active-route-change="setActive($event)"
                            >
                                <BriefcaseIcon />
                                <template #expander>
                                    <ButtonTriangle class="ml-2" :is-active="isActive" @click="toggleChildren" />
                                </template>
                            </LeftNavItem>
                        </template>
                        <template #children>
                            <ul v-if="adminUser || featureFlags.employerReports">
                                <LeftNavItemChild
                                    :item="{
                                        title: EMPLOYER_NETWORK_BENCHMARKING_AND_DISRUPTION_TITLE,
                                        to: '/employer/employer-network-report',
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>

                            <ul v-if="adminUser || featureFlags.employerReports">
                                <LeftNavItemChild
                                    :item="{
                                        title: 'Census Generator',
                                        to: '/employer/census-generator',
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>

                            <ul v-if="adminUser || currentUser.isDashboardConfigured('payment-integrity')">
                                <LeftNavItemChild
                                    :item="{
                                        title: 'Payment Integrity',
                                        to: '/employer/payment-integrity',
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>
                        </template>
                    </NavExpander>

                    <NavExpander :is-expanded="isExpanded">
                        <template #default="{ toggleChildren, isActive, setActive }">
                            <LeftNavItem
                                :item="{
                                    title: 'Inventory',
                                    to: '/inventory',
                                }"
                                :is-expanded="isExpanded"
                                @active-route-change="setActive($event)"
                            >
                                <ShieldCheckIcon />
                                <template #expander>
                                    <ButtonTriangle class="ml-2" :is-active="isActive" @click="toggleChildren" />
                                </template>
                            </LeftNavItem>
                        </template>
                        <template #children>
                            <ul>
                                <LeftNavItemChild
                                    key="inventory-payers"
                                    :item="{
                                        title: 'Payers',
                                        to: {
                                            name: 'inventory-payers-index',
                                        },
                                    }"
                                    :is-expanded="isExpanded"
                                />
                            </ul>
                        </template>
                    </NavExpander>

                    <LeftNavItemButton
                        v-if="adminUser"
                        :item="{
                            title: 'Provider Search',
                            to: '/provider-search',
                        }"
                        :is-expanded="isExpanded"
                        @click="openProviderDirectory"
                    >
                        <FileSearchIcon />
                    </LeftNavItemButton>
                </div>
            </div>

            <div v-if="adminUser" class="relative flex w-24 justify-center">
                <HoverCardRoot v-model:open="adminHoverState" :open-delay="100">
                    <HoverCardTrigger
                        class="bg-brand-purple-100 text-brand-purple-700 hover:bg-brand-purple-200 flex size-10 cursor-pointer items-center justify-center rounded-lg"
                    >
                        <div>
                            <LockIcon class="size-4" aria-hidden="true" />
                        </div>

                        <span
                            :class="{
                                'pointer-events-none -translate-x-3 opacity-0 duration-200': !isExpanded,
                                'pointer-events-auto opacity-100 duration-500': isExpanded,
                            }"
                            class="title text-brand-purple-700 absolute top-0 bottom-0 left-24 -ml-7 flex w-64 cursor-pointer items-center pl-3 text-sm font-medium transition-all ease-out"
                            >Manage
                        </span>
                    </HoverCardTrigger>
                    <HoverCardPortal>
                        <HoverCardContent
                            class="z-501 w-80 rounded-xl border bg-white py-4 shadow-xs"
                            side="top"
                            :side-offset="3"
                            :collision-padding="20"
                        >
                            <div class="pr-4 pb-4" @click.stop="closeAdminPanel">
                                <NavExpander :is-expanded="true" :force-expanded="true">
                                    <template #default="{ setActive }">
                                        <LeftNavItem
                                            :item="{
                                                title: 'MRF Data Sources',
                                                to: '/sources',
                                            }"
                                            :is-expanded="true"
                                            @active-route-change="setActive($event)"
                                        >
                                            <ServerIcon />
                                        </LeftNavItem>
                                    </template>
                                    <template #children>
                                        <ul>
                                            <LeftNavItemChild
                                                :item="{
                                                    title: 'Payors',
                                                    to: '/sources/payors',
                                                }"
                                                :is-expanded="true"
                                            />
                                            <ul class="-mt-1 ml-1">
                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Manage',
                                                        to: '/sources/payors',
                                                    }"
                                                    :is-expanded="true"
                                                />

                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Network Audits',
                                                        to: '/sources/audits/networks',
                                                    }"
                                                    :is-expanded="true"
                                                />
                                            </ul>

                                            <LeftNavItemChild
                                                :item="{
                                                    title: 'Health Systems',
                                                    to: '/sources/health-systems',
                                                }"
                                                :is-expanded="true"
                                            />
                                            <ul class="-mt-1 ml-1">
                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Manage',
                                                        to: '/sources/health-systems',
                                                    }"
                                                    :is-expanded="true"
                                                />

                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Location Audits',
                                                        to: '/sources/audits/locations',
                                                    }"
                                                    :is-expanded="true"
                                                />

                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Locations',
                                                        to: '/sources/locations',
                                                    }"
                                                    :is-expanded="true"
                                                />

                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Location Templates',
                                                        to: '/sources/location-templates',
                                                    }"
                                                    :is-expanded="true"
                                                />
                                            </ul>

                                            <LeftNavItemChild
                                                :item="{
                                                    title: 'TPAs',
                                                    to: '/sources/tpas',
                                                }"
                                                :is-expanded="true"
                                            />
                                            <ul class="-mt-1 ml-1">
                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Manage',
                                                        to: '/sources/tpas',
                                                    }"
                                                    :is-expanded="true"
                                                />

                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Generation Report',
                                                        to: '/sources/mrf-generations',
                                                    }"
                                                    :is-expanded="true"
                                                />
                                            </ul>
                                            <LeftNavItemChild
                                                :item="{
                                                    title: 'Data Ops Dashboards',
                                                    to: '/sources/data-ops',
                                                }"
                                                :is-expanded="true"
                                            />
                                            <ul class="-mt-1 ml-1">
                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Indexes',
                                                        to: '/sources/data-ops/indexes',
                                                    }"
                                                    :is-expanded="true"
                                                />

                                                <LeftNavItemTertiary
                                                    :item="{
                                                        title: 'Networks & Locations',
                                                        to: '/sources/data-ops/networks-locations',
                                                    }"
                                                    :is-expanded="true"
                                                />
                                            </ul>
                                        </ul>
                                    </template>
                                </NavExpander>
                            </div>

                            <div class="pr-4 pb-4" @click.capture="closeAdminPanel">
                                <LeftNavItem
                                    :item="{
                                        title: 'Data Ops',
                                        to: '/data-ops',
                                    }"
                                    :is-expanded="true"
                                >
                                    <Gauge />
                                </LeftNavItem>
                            </div>

                            <div class="border-t border-slate-100 pt-4 pr-4" @click.stop="closeAdminPanel">
                                <LeftNavItem
                                    v-if="adminUser"
                                    :item="{
                                        title: 'Accounts',
                                        to: '/accounts',
                                    }"
                                    :is-expanded="true"
                                >
                                    <UsersIcon />
                                </LeftNavItem>
                            </div>

                            <HoverCardArrow class="-mt-px fill-white stroke-gray-300" :width="12" :height="6" />
                        </HoverCardContent>
                    </HoverCardPortal>
                </HoverCardRoot>
            </div>
            <NotificationsPopover
                v-if="featureFlags.signalWatchNotifications"
                v-slot="{ open }"
                v-model:open="notificationsPopover.isOpen"
                :view="notificationsPopover.view"
                side="right"
                :side-offset="-20"
            >
                <LeftNavItemButtonReversed
                    class="notifications-nav-item"
                    :item="{ title: 'Notifications' }"
                    :is-expanded="isExpanded"
                    :is-active="open"
                >
                    <span class="relative inline-flex">
                        <Bell />
                        <NotificationsIndicator class="absolute -top-px right-2.5" :has-notifications="!!unreadCount" />
                    </span>
                </LeftNavItemButtonReversed>
            </NotificationsPopover>

            <LeftNavItemButtonReversed
                :item="{
                    title: 'Users',
                    to: '/settings/users',
                }"
                :is-expanded="isExpanded"
                :is-active="router.currentRoute.value.name === 'settings-users'"
                @click="router.push('/settings/users')"
            >
                <Users />
            </LeftNavItemButtonReversed>

            <LeftNavItemButtonReversed
                :item="{
                    title: 'Help',
                    to: '/help',
                }"
                :is-expanded="isExpanded"
                @click="toggle"
            >
                <CircleXIcon v-if="isShown" />
                <MessageCircleQuestionMark v-else />
            </LeftNavItemButtonReversed>

            <LeftNavItem
                :item="{
                    title: currentUser.email,
                    to: '/sign-out',
                }"
                :is-expanded="isExpanded"
                class="tooltip"
            >
                <LogOutIcon />
                <span class="tooltiptext">Sign Out</span>
            </LeftNavItem>
        </template>

        <template #after="{ isExpanded, setExpanded }">
            <button
                v-if="isSignalRoute && !isExpanded"
                class="fixed top-0 left-0 z-501 flex h-12 w-10 items-center justify-center text-slate-400 transition-colors hover:text-slate-700 print:hidden"
                @click="setExpanded(true)"
            >
                <Menu class="h-4 w-4" />
            </button>
        </template>
    </LeftNavShell>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ButtonTriangle from '@/components/ButtonTriangle.vue'
import NavExpander from '@/components/NavExpander.vue'
import LeftNavShell from '@/components/LeftNavShell.vue'
import LeftNavImpersonationIndicator from '@/components/LeftNavImpersonationIndicator.vue'
import LeftNavItem from '@/components/LeftNavItem.vue'
import LeftNavItemButton from '@/components/LeftNavItemButton.vue'
import LeftNavItemButtonReversed from '@/components/LeftNavItemButtonReversed.vue'
import LeftNavItemChild from '@/components/LeftNavItemChild.vue'
import { EMPLOYER_NETWORK_BENCHMARKING_AND_DISRUPTION_TITLE } from '@/features/employer/config/constants'
import { useAnalytics } from '@/composables/useAnalytics.js'
import { useCurrentUserStore } from '@/stores/useCurrentUserStore'
import { useModalProviderSearch } from '@/composables/useModalProviderSearch'

import AmplitudeLogoIcon from '@/assets/svg/amplitude-logo.svg'
import SignalLogoIcon from '@/assets/svg/signal-logo-take-two.svg'
import { usePylon } from '@/composables/pylon/usePylon'

import { HoverCardArrow, HoverCardContent, HoverCardPortal, HoverCardRoot, HoverCardTrigger } from 'reka-ui'
import {
    Bell,
    Code,
    LockIcon,
    MessageCircleQuestionMark,
    Users,
    Menu,
    Gauge,
    UsersIcon,
    ChartColumnBigIcon,
    BriefcaseIcon,
    LogOutIcon,
    ServerIcon,
    ShieldCheckIcon,
    ChartPieIcon,
    HouseIcon,
    FileSearchIcon,
    CircleXIcon,
    PresentationIcon,
} from 'lucide-vue-next'

const adminHoverState = ref(false)
const scrollEl = ref(null)
const isScrolledToBottom = ref(false)
const isScrolledToTop = ref(true)

function onNavScroll() {
    if (!scrollEl.value) return
    const { scrollTop, scrollHeight, clientHeight } = scrollEl.value
    isScrolledToBottom.value = scrollTop + clientHeight >= scrollHeight - 4
    isScrolledToTop.value = scrollTop <= 4
}

const router = useRouter()

const currentUser = useCurrentUserStore()
const modalProviderSearch = useModalProviderSearch()
const { analytics, featureFlags } = useAnalytics()
const { unreadCount } = useUserNotifications()
const notificationsPopover = useNotificationsPopoverStore()
useNotificationsSettingsDeepLink()

const { toggle, isShown } = usePylon()

const adminUser = currentUser.isAdmin
const route = useRoute()
const isSignalRoute = computed(() => route.name?.startsWith('signal-'))

function openProviderDirectory() {
    modalProviderSearch.launch()

    analytics.track({
        event: 'Provider Directory Opened From Nav',
    })
}

function closeAdminPanel() {
    adminHoverState.value = false
}
</script>

<style scoped>
@reference "@/tailwind.css";

/* override the shared blue active state for this one nav item only */
.notifications-nav-item :deep(.LeftNavItem.Button.router-link-active) {
    @apply bg-pink-50 text-pink-700;
}
.notifications-nav-item :deep(.LeftNavItem.Button.router-link-active) .title {
    @apply text-pink-800;
}

.tooltip {
    position: relative;
}

.tooltip .tooltiptext {
    visibility: hidden;
    width: 120px;
    background-color: black;
    color: #fff;
    text-align: center;
    border-radius: 6px;
    padding: 10px 0;

    /* Position the tooltip */
    position: absolute;
    bottom: 120%;
    left: 0%;
    margin-right: -15px;
    z-index: 1;
}

.tooltip .tooltiptext::after {
    content: ' ';
    position: absolute;
    top: 100%;
    /* At the top of the tooltip */
    right: 74%;
    margin-left: 0px;
    border-width: 5px;
    border-style: solid;
    border-color: black transparent transparent transparent;
}

.tooltip:hover .tooltiptext {
    visibility: visible;
}
</style>
